/**
 * O botão de gravar da barra (só do atendente que criou a chamada; docs/specs/otto/RECORDING.md PRD AC-20).
 *
 * Estados: pronto ("Gravar a chamada"), esperando o aceite dos convidados ("Cancelar o pedido de gravação"), gravando
 * ("Parar a gravação") e TRAVADO com o motivo (plano sem gravação, cota do mês usada). O botão mostra o uso ("3 de 5
 * gravações neste mês") e avisa quando a próxima é a última. Travar a gravação nunca interrompe a chamada em andamento.
 *
 * A dica é CSS dentro do próprio botão (aparece no hover e no foco), de propósito: a sala também roda na janela flutuante
 * (outro documento) e o tooltip do Radix iria para o `body` da janela principal.
 */
import { Circle, Loader2, Square } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FOCUS_RING, ROUND_BUTTON, SIZE } from '../room/controlStyles';
import { useCallRecording } from './recordingContext';
import { ROOM_RECORDING_STATE } from './recordingProtocol';
import { LAST_ONE_TEXT, LOCKED_REASON_TEXT, usageLabel } from './recordingCopy';

const RECORDING_TONE = 'bg-red-600 text-white ring-1 ring-red-300/50 hover:bg-red-500';
const REQUESTED_TONE = 'bg-amber-400 text-black ring-1 ring-amber-200/60 hover:bg-amber-300';

/** @param {{ variant: keyof typeof SIZE }} props */
export function RecordButton({ variant }) {
    const recording = useCallRecording();
    const agent = recording?.agent;
    // Sem o painel (ainda carregando, ou a gravação desligada neste ambiente) não há o que oferecer
    if (!agent?.enabled || !agent.canControl) return null;

    const state = recording.state;
    const locked = state === ROOM_RECORDING_STATE.NONE && !agent.available;
    const label = state === ROOM_RECORDING_STATE.RECORDING ? 'Parar a gravação'
        : state === ROOM_RECORDING_STATE.REQUESTED ? 'Cancelar o pedido de gravação'
            : locked ? 'Gravação indisponível' : 'Gravar a chamada';
    const lockedReason = locked ? LOCKED_REASON_TEXT[agent.reason] ?? LOCKED_REASON_TEXT.RECORDING_UNAVAILABLE : null;
    const usage = agent.reason === 'RECORDING_NOT_IN_PLAN' || agent.reason === 'RECORDING_UNAVAILABLE' ? null : usageLabel(agent);
    const onClick = state === ROOM_RECORDING_STATE.NONE ? agent.request : agent.stop;
    const Icon = agent.busy ? Loader2 : state === ROOM_RECORDING_STATE.RECORDING ? Square : Circle;

    return (
        <span className="group relative inline-flex" data-record-control="">
            <button
                type="button"
                aria-label={label}
                aria-describedby={`record-tip-${variant}`}
                disabled={locked || agent.busy}
                onClick={onClick}
                className={cn(
                    ROUND_BUTTON, SIZE[variant].btn, FOCUS_RING, 'disabled:cursor-not-allowed disabled:opacity-50',
                    state === ROOM_RECORDING_STATE.RECORDING && RECORDING_TONE,
                    state === ROOM_RECORDING_STATE.REQUESTED && REQUESTED_TONE,
                )}
            >
                <Icon
                    className={cn(SIZE[variant].icon, agent.busy && 'animate-spin', state === ROOM_RECORDING_STATE.NONE && !agent.busy && 'fill-red-500 text-red-500')}
                    aria-hidden="true"
                />
            </button>
            <span
                id={`record-tip-${variant}`}
                role="tooltip"
                data-record-tooltip=""
                className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-[240px] -translate-x-1/2 rounded-md bg-black/90 px-2.5 py-1.5 text-center text-xs font-medium text-neutral-50 opacity-0 shadow-lg ring-1 ring-white/10 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
            >
                <span className="block">{lockedReason ?? label}</span>
                {usage && <span className="block text-neutral-300">{usage}</span>}
                {agent.lastOne && state === ROOM_RECORDING_STATE.NONE && !locked && (
                    <span className="block text-amber-300">{LAST_ONE_TEXT}</span>
                )}
            </span>
        </span>
    );
}
